import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';
import type { AbsenceMark } from './journal';

export interface CatchUpItem {
  lessonId: string;
  courseId: string;
  date: string;
  subject: string;
  topic: string | null;
  mark: AbsenceMark;
  homework: { id: string; title: string; dueOn: string; done: boolean } | null;
  materials: number;
  boards: number;
  /** an open test of the subject within a week that the student has not answered */
  test: { title: string; dueOn: string } | null;
  done: boolean;
}

/** What is not caught up yet comes first: lessons whose subject has a test soon, then the oldest. */
export function catchUpOrder(items: ReadonlyArray<CatchUpItem>): CatchUpItem[] {
  return [...items].sort(
    (a, b) =>
      Number(a.done) - Number(b.done) ||
      Number(b.test !== null) - Number(a.test !== null) ||
      a.date.localeCompare(b.date),
  );
}

/** Missed lessons of the last weeks with their homework, materials and boards (student, family). */
export function useCatchUpPlan(studentId: string, days = 21) {
  const db = useDb();
  return useQuery({
    queryKey: ['catch-up', studentId, days],
    queryFn: async (): Promise<CatchUpItem[]> =>
      catchUpOrder(
        must(await db.rpc('catch_up_plan', { p_student: studentId, p_days: days })).map((r) => ({
          lessonId: r.lesson_id,
          courseId: r.course_id,
          date: r.date,
          subject: r.subject,
          topic: r.topic,
          mark: r.mark,
          homework:
            r.homework_id && r.homework_title && r.homework_due
              ? {
                  id: r.homework_id,
                  title: r.homework_title,
                  dueOn: r.homework_due,
                  done: r.homework_done,
                }
              : null,
          materials: r.materials,
          boards: r.boards,
          test: r.test_title && r.test_due ? { title: r.test_title, dueOn: r.test_due } : null,
          done: r.done,
        })),
      ),
  });
}

/** The student ticks a missed lesson off («готово») or takes the tick back. */
export function useCatchUpMark() {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (a: {
      schoolId: string;
      studentId: string;
      lessonId: string;
      done: boolean;
    }) =>
      a.done
        ? must(
            await db
              .from('catch_up_marks')
              .upsert(
                { school_id: a.schoolId, student_member_id: a.studentId, lesson_id: a.lessonId },
                { onConflict: 'student_member_id,lesson_id', ignoreDuplicates: true },
              ),
          )
        : must(
            await db
              .from('catch_up_marks')
              .delete()
              .eq('student_member_id', a.studentId)
              .eq('lesson_id', a.lessonId),
          ),
    onSettled: () => client.invalidateQueries({ queryKey: ['catch-up'] }),
  });
}
